import { create } from 'zustand';
import apiClient from '../lib/api';

export interface PayrollDeductions {
  tax?: number;
  insurance?: number;
  retirement?: number;
  other?: number;
}
export interface PayrollAllowances {
  housing?: number;
  transport?: number;
  other?: number;
}

export interface PayrollRecord {
  id: string;
  employeeId: string;
  periodStart: string;
  periodEnd: string;
  basicSalary: number;
  grossSalary: number;
  netSalary: number;
  allowances: PayrollAllowances;
  deductions: PayrollDeductions;
  status: 'draft' | 'processed' | 'paid' | 'cancelled';
  paymentDate?: string | null;
  paymentReference?: string | null;
  notes?: string | null;
  createdAt: string;
}

export interface PayrollStatBucket { count: number; amount: number }
export interface PayrollStats {
  draft: PayrollStatBucket;
  processed: PayrollStatBucket;
  paid: PayrollStatBucket;
  total: PayrollStatBucket;
}

export interface GeneratePayrollInput {
  employeeId: string;
  periodStart: string;
  periodEnd: string;
  basicSalary: number;
  allowances: PayrollAllowances;
  deductions: PayrollDeductions;
  grossSalary: number;
  netSalary: number;
  notes?: string;
}

interface PayrollState {
  records: PayrollRecord[];
  stats: PayrollStats | null;
  isLoading: boolean;
  error: string | null;

  fetchPayroll: (filters?: any) => Promise<void>;
  fetchStats: () => Promise<void>;
  generatePayroll: (data: GeneratePayrollInput) => Promise<void>;
  approvePayroll: (id: string) => Promise<void>;
  markPaid: (id: string, payment: { paymentDate: string; paymentReference: string }) => Promise<void>;
  clearError: () => void;
}

function toObject(v: any): any {
  if (!v) return {};
  if (typeof v === 'string') {
    try { return JSON.parse(v); } catch { return {}; }
  }
  return v;
}

// The service returns raw snake_case rows.
function normalizeRecord(r: any): PayrollRecord {
  return {
    id: r.id,
    employeeId: r.employee_id,
    periodStart: r.period_start,
    periodEnd: r.period_end,
    basicSalary: Number(r.basic_salary) || 0,
    grossSalary: Number(r.gross_salary) || 0,
    netSalary: Number(r.net_salary) || 0,
    allowances: toObject(r.allowances),
    deductions: toObject(r.deductions),
    status: r.status,
    paymentDate: r.payment_date,
    paymentReference: r.payment_reference,
    notes: r.notes,
    createdAt: r.created_at,
  };
}

export const usePayrollStore = create<PayrollState>((set, get) => ({
  records: [],
  stats: null,
  isLoading: false,
  error: null,

  fetchPayroll: async (filters?: any) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/people/payroll', { params: filters });
      const rows = response.data?.data ?? [];
      set({ records: rows.map(normalizeRecord), isLoading: false });
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to fetch payroll', isLoading: false });
    }
  },

  fetchStats: async () => {
    try {
      const response = await apiClient.get('/people/payroll/stats');
      set({ stats: response.data?.data ?? response.data });
    } catch (error: any) {
      console.error('Failed to fetch payroll stats', error);
    }
  },

  generatePayroll: async (data) => {
    set({ error: null });
    try {
      await apiClient.post('/people/payroll/generate', data);
      await Promise.all([get().fetchPayroll(), get().fetchStats()]);
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to generate payroll' });
      throw error;
    }
  },

  approvePayroll: async (id: string) => {
    set({ error: null });
    try {
      await apiClient.post(`/people/payroll/${id}/approve`);
      await Promise.all([get().fetchPayroll(), get().fetchStats()]);
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to approve payroll' });
      throw error;
    }
  },

  markPaid: async (id: string, payment) => {
    set({ error: null });
    try {
      await apiClient.post(`/people/payroll/${id}/mark-paid`, payment);
      await Promise.all([get().fetchPayroll(), get().fetchStats()]);
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to mark payroll as paid' });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
